import type { ParsedStatement, ParsedTransaction } from "@paisa/types"
import * as XLSX from "xlsx"
import { parseAmount } from "../lib/parse-helpers"

// ── Detection ────────────────────────────────────────────────────────────

/**
 * Detect whether an XLS or CSV file is an HDFC Swiggy co-branded credit card statement.
 *
 * The XLS/CSV files share the same detection logic.
 * - XLS raw content contains the sheet name "Statement" and card branding text
 * - CSV delimiter is "~|~" and the header row contains "Transaction type" and "AMT"
 */
export function isSwiggyhdfcCreditCard(rawContent: string, headers?: string[]): boolean {
  // Explicit Swiggy HDFC branding
  if (/Swiggy HDFC/i.test(rawContent)) return true

  // ~|~ delimited CSV: unique delimiter + transaction header is a definitive fingerprint
  if (rawContent.includes("~|~") && /Transaction type/i.test(rawContent)) return true

  // XLS: credit card number + transaction headers in content
  const hasCreditCardNo = /(?:Credit\s+)?Card No\.?[:.]?\s*[\dX ]+\d{4}/i.test(rawContent)
  const hasTransactionType =
    /Transaction type/i.test(rawContent) ||
    (!!headers && headers.some((h) => /Transaction type/i.test(h)))
  const hasAmt =
    /\bAMT\b/.test(rawContent) || (!!headers && headers.some((h) => h.trim() === "AMT"))

  return hasCreditCardNo && hasTransactionType && hasAmt
}

// ── XLS Parser ──────────────────────────────────────────────────────────

/**
 * Parse an HDFC Swiggy credit card XLS statement.
 *
 * Format (sparse merged-cell layout):
 *   Sheet: "Statement"
 *   Row 5 col 4:   "20 Mar, 2026"       (statement date)
 *   Row 2 col 13:  "Credit Card No.: 526873XXXXXX4388"
 *   Row 17:        column headers (sparse — Transaction type at col 0,
 *                  Date & Time at col 9, Description at col 12,
 *                  AMT at col 20, Debit / Credit at col 23)
 *   Row 18+:       transaction data
 *   End:           row where col 0 = "Reward Points Summary"
 */
export function parseSwiggyhdfcCreditCard(data: ArrayBuffer): ParsedStatement {
  const workbook = XLSX.read(new Uint8Array(data), { type: "array" })

  const sheetName = workbook.SheetNames.find((n) => n === "Statement") ?? workbook.SheetNames[0]
  const sheet = workbook.Sheets[sheetName]
  if (!sheet) throw new Error("No sheet found in HDFC Swiggy workbook")

  const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" })

  const metadata = extractXlsMetadata(rows)
  const transactions = parseXlsTransactions(rows)

  return {
    bank: "hdfc",
    accountType: "credit_card",
    accountNumber: metadata.accountLast4,
    periodStart: computePeriodStart(transactions),
    periodEnd: metadata.statementDate,
    transactions,
  }
}

// ── XLS helpers ───────────────────────────────────────────────────────────

interface XlsMetadata {
  accountLast4: string | null
  statementDate: string | null
}

function extractXlsMetadata(rows: unknown[][]): XlsMetadata {
  let accountLast4: string | null = null
  let statementDate: string | null = null

  // Scan first 20 rows for metadata
  for (let i = 0; i < Math.min(20, rows.length); i++) {
    const row = rows[i] ?? []

    for (const cell of row) {
      const s = String(cell ?? "").trim()

      // "Credit Card No.: 526873XXXXXX4388" → last 4
      const cardMatch = s.match(/Credit Card No\.\s*:\s*[\d X]+(\d{4})/)
      if (cardMatch) {
        accountLast4 = cardMatch[1]
      }

      // "20 Mar, 2026" statement date (row 5, col 4 — but scan broadly)
      const dateMatch = s.match(/^(\d{1,2})\s+(\w+),\s+(\d{4})$/)
      if (dateMatch) {
        const parsed = parseXlsDate(s)
        if (parsed) statementDate = parsed
      }
    }
  }

  return { accountLast4, statementDate }
}

function parseXlsTransactions(rows: unknown[][]): ParsedTransaction[] {
  // Header row is row 17 (0-indexed) — find it by looking for "Transaction type"
  const HEADER_COL = 0
  let headerIdx = -1
  for (let i = 0; i < Math.min(30, rows.length); i++) {
    const row = rows[i] ?? []
    if (String(row[HEADER_COL] ?? "").trim() === "Transaction type") {
      headerIdx = i
      break
    }
  }
  if (headerIdx === -1) throw new Error("Could not find header row in HDFC Swiggy XLS")

  // Fixed column positions (sparse layout)
  const COL_TYPE = 0
  const COL_DATE = 9
  const COL_DESC = 12
  const COL_AMT = 20
  const COL_DR_CR = 23

  const transactions: ParsedTransaction[] = []

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i] ?? []

    // Stop at summary section
    if (String(row[COL_TYPE] ?? "").trim() === "Reward Points Summary") break

    const rawDate = String(row[COL_DATE] ?? "").trim()
    if (!rawDate) continue

    // Date format: "21/02/2026 / 00:00" — take everything before " /"
    const datePart = rawDate.split(" /")[0].trim()
    const date = parseDdMmYyyy(datePart)
    if (!date) continue

    const description = String(row[COL_DESC] ?? "").trim()
    if (!description) continue

    const rawAmt = String(row[COL_AMT] ?? "").trim()
    const amount = parseAmount(rawAmt)
    if (amount === 0) continue

    const drCr = String(row[COL_DR_CR] ?? "").trim()
    const type: "debit" | "credit" = drCr === "Cr" ? "credit" : "debit"

    transactions.push({
      date,
      description,
      amount,
      type,
      referenceNumber: null,
      valueDate: null,
      closingBalance: null,
      paymentMethod: null,
      extractedName: description,
      upiId: null,
      sourceCategory: null,
    })
  }

  return transactions
}

// ── CSV Parser ───────────────────────────────────────────────────────────

/**
 * Parse an HDFC Swiggy credit card CSV statement.
 *
 * Format:
 *   Delimiter: "~|~"
 *   Metadata section contains:
 *     "Statement Date~|~20/03/2026"
 *     Free-text line with "Card No: 5268 73XX XXXX 4388"
 *   Transaction header: "Transaction type~|~...~|~DATE~|~Description~|~AMT~|~Debit /Credit~|~"
 *   Transaction rows:   "Domestic~|~NIMIT HARIA~|~21/02/2026 00:00:00~|~...~|~907.50~|~Cr~|~"
 *   End: line containing "Cashback Summary"
 */
export function parseSwiggyhdfcCreditCardCsv(text: string): ParsedStatement {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)

  let accountLast4: string | null = null
  let statementDate: string | null = null
  const transactions: ParsedTransaction[] = []

  let inTransactions = false

  for (const line of lines) {
    // Stop parsing transactions at Cashback Summary section
    if (/Cashback Summary/i.test(line)) break

    const cols = line.split("~|~")

    // Try to extract metadata from non-transaction lines
    if (!inTransactions) {
      // "Statement Date~|~20/03/2026"
      if (cols[0]?.trim() === "Statement Date" && cols[1]) {
        statementDate = parseDdMmYyyy(cols[1].trim())
      }

      // Card number from free-text line: "Card No: 5268 73XX XXXX 4388"
      const cardMatch = line.match(/Card No[.:]?\s*([\d X]+(\d{4}))/i)
      if (cardMatch) {
        // Extract last 4 digits from the card number string
        const digits = cardMatch[1].replace(/\s/g, "")
        accountLast4 = digits.slice(-4)
      }

      // Detect transaction header row
      if (cols[0]?.trim() === "Transaction type" && cols.some((c) => c.trim() === "AMT")) {
        inTransactions = true
      }
    } else {
      // Parse transaction row
      // cols: [type, name, date, description, amount, dr/cr, (empty)]
      if (cols.length < 6) continue

      const rawDate = cols[2]?.trim() ?? ""
      // "21/02/2026 00:00:00" → take date part only
      const datePart = rawDate.split(" ")[0]
      const date = parseDdMmYyyy(datePart)
      if (!date) continue

      const description = cols[3]?.trim() ?? ""
      if (!description) continue

      const amount = parseAmount(cols[4]?.trim() ?? "")
      if (amount === 0) continue

      const drCr = cols[5]?.trim() ?? ""
      const type: "debit" | "credit" = drCr === "Cr" ? "credit" : "debit"

      transactions.push({
        date,
        description,
        amount,
        type,
        referenceNumber: null,
        valueDate: null,
        closingBalance: null,
        paymentMethod: null,
        extractedName: description,
        upiId: null,
        sourceCategory: null,
      })
    }
  }

  return {
    bank: "hdfc",
    accountType: "credit_card",
    accountNumber: accountLast4,
    periodStart: computePeriodStart(transactions),
    periodEnd: statementDate,
    transactions,
  }
}

// ── Utilities ────────────────────────────────────────────────────────────

/** Earliest transaction date, or null if no transactions */
function computePeriodStart(transactions: ParsedTransaction[]): string | null {
  if (transactions.length === 0) return null
  return transactions.reduce((min, t) => (t.date < min ? t.date : min), transactions[0].date)
}

/**
 * Parse "DD/MM/YYYY" → "YYYY-MM-DD"
 */
function parseDdMmYyyy(raw: string): string | null {
  const match = raw.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (!match) return null
  const day = match[1].padStart(2, "0")
  const month = match[2].padStart(2, "0")
  return `${match[3]}-${month}-${day}`
}

/**
 * Parse XLS date format "20 Mar, 2026" → "2026-03-20"
 */
function parseXlsDate(raw: string): string | null {
  const MONTHS: Record<string, string> = {
    jan: "01",
    feb: "02",
    mar: "03",
    apr: "04",
    may: "05",
    jun: "06",
    jul: "07",
    aug: "08",
    sep: "09",
    oct: "10",
    nov: "11",
    dec: "12",
  }
  const match = raw.trim().match(/^(\d{1,2})\s+(\w+),?\s+(\d{4})$/)
  if (!match) return null
  const month = MONTHS[match[2].toLowerCase().slice(0, 3)]
  if (!month) return null
  const day = match[1].padStart(2, "0")
  return `${match[3]}-${month}-${day}`
}
